/**
 * ErrorBoundary — Batch #14.2
 *
 * Catches ReferenceError, TypeError, and other runtime crashes that would otherwise
 * cause silent white screens. Displays a user-friendly fallback UI with:
 * - Clear error message
 * - Reload button
 * - Optional "Contact support" hint
 * - Console error details (visible in dev tools for debugging)
 *
 * Wrapped around the entire app in App.js to prevent crashes from cascading.
 *
 * Inspired by React docs : https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
import React from "react";
import { AlertCircle, RefreshCw, Home } from "lucide-react";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console for dev tools debugging
    console.error("[ErrorBoundary] Caught React crash:", error);
    console.error("[ErrorBoundary] Component stack:", errorInfo?.componentStack);
    this.setState({ errorInfo });

    // Optional : send to backend telemetry if available (silent fail if endpoint down)
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      fetch(`${API}/client-error-log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "react_error_boundary",
          error_name: error?.name || "Unknown",
          error_message: error?.message || String(error),
          component_stack: errorInfo?.componentStack?.slice(0, 4000) || "",
          url: window.location.href,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }),
      }).catch(() => {});
    } catch (_e) { /* silent */ }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";
    const err = this.state.error;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="error-boundary-fallback">
        <div className="bg-white border border-rose-200 rounded-2xl p-6 max-w-lg w-full shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-slate-800 mb-1">Une erreur est survenue</h1>
              <p className="text-sm text-slate-600 mb-3">
                L'application a rencontré un problème inattendu. Vous pouvez essayer de recharger la page,
                retourner à l'accueil, ou contacter votre cabinet comptable si le problème persiste.
              </p>

              {isDev && err && (
                <details className="mb-3 rounded-lg bg-slate-50 border border-slate-200 p-2 text-[10px] font-mono text-slate-600">
                  <summary className="cursor-pointer text-rose-700 font-semibold">Détails technique (dev)</summary>
                  <div className="mt-2 break-all whitespace-pre-wrap" data-testid="error-boundary-details">
                    <b>{err.name || "Error"}</b> : {err.message}
                    {this.state.errorInfo?.componentStack && (
                      <div className="mt-1 text-slate-500">{this.state.errorInfo.componentStack.slice(0, 600)}…</div>
                    )}
                  </div>
                </details>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={this.handleReload}
                  className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center gap-1.5"
                  data-testid="error-boundary-reload"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Recharger la page
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold inline-flex items-center gap-1.5"
                  data-testid="error-boundary-home"
                >
                  <Home className="w-3.5 h-3.5" /> Accueil
                </button>
              </div>

              <p className="text-[10px] text-slate-400 mt-3">
                Code erreur : {err?.name || "RuntimeError"} · {new Date().toLocaleString("fr-CA")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
